import FormField from 'components/FormField';
import {
  AiModelState,
  getPrefixName,
  hideRadioClassName,
  Prefix,
  stores2storeScopes,
} from './share';
import { Radio, Select } from 'antd';
import { useCallback, useMemo } from 'react';
import { updateSpecifyExpressType } from 'views/BootGuide/service';
import { getStoreCount } from 'components/StoreScopes';
import useSmartField from 'hooks/useSmartField';
import ModalMode from './ModalMode';
import { useTranslate } from '@tolgee/react';

type FV = BootGuide.AiModel.FormValues.SpecifyExpressType;

export function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      noSupport: t('尾程物流商由平台统一分配，买家无法指定\n'),
      support: t('本店支持指定快递种类'),
    } as const;
  }, [t]);
}

function SpecifyExpressType({ prefix, singleMode }: { prefix?: Prefix; singleMode?: boolean }) {
  const label = useLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));

  const getHideStyle = (type: FV['value']) =>
    singleMode && value !== type ? { display: 'none' } : undefined;

  return (
    <div className="mt-[6px]">
      {Object.keys(label).map((key) => {
        return (
          <div key={key} className="mb-2" style={getHideStyle(key)}>
            <Radio
              className={singleMode ? hideRadioClassName : undefined}
              checked={value === key}
              onChange={({ target: { checked } }) => checked && onChange(key)}
            >
              {label[key]}
            </Radio>
          </div>
        );
      })}
      <DeliveryTypes prefix={prefix} />
    </div>
  );
}

function DeliveryTypes({ prefix }: { prefix?: Prefix }) {
  const { t } = useTranslate();

  const {
    field: { value },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));

  if (value !== 'support' || !getStoreCount(storeScopes)) {
    return <></>;
  }

  return (
    <FormField<FV['deliveryTypes']>
      name={getPrefixName('deliveryTypes', prefix)}
      validate={(value = []) =>
        value.length || !getStoreCount(storeScopes) ? undefined : t('请选择指定快递种类')
      }
    >
      {({ field }) => {
        return (
          <Select
            showArrow
            mode="tags"
            placeholder={t('请选择指定快递种类')}
            options={expressOptions}
            {...field}
          />
        );
      }}
    </FormField>
  );
}

export function SpecifyExpressTypeBootGuide(props: AiModelState<FV>) {
  const label = useLabel();

  return (
    <ModalMode
      state={props}
      title={props.t('指定快递种类')}
      tips={props.t('您的店铺是否支持买家指定快递种类呢?以下是一些常见的选项供您选择和调整\n')}
      completeMinute={2}
      form={<SpecifyExpressType />}
      getFirstInitialValues={useCallback(({ stores }): { values: FV[] } => {
        return {
          values: [
            { value: 'noSupport', storeScopes: stores2storeScopes(stores), deliveryTypes: [] },
            { value: 'support', storeScopes: [], deliveryTypes: [] },
          ],
        };
      }, [])}
      updateApi={updateSpecifyExpressType}
      getFirstFormItem={({ value }, index, storeSelect) => {
        return (
          <div>
            <section>
              {label[value]}
              {storeSelect}
            </section>
            <DeliveryTypes prefix={['values', index]} />
          </div>
        );
      }}
      getFirstFormStoreSelectKey={({ value }) => value}
    />
  );
}

const expressOptions = [
  'Correios',
  'Expresso padrão',
  'Shopee Entrega Direta',
  'Shopee Xpress',
  'Shopee Xpress CPF',
  'Entrega Económica',
  'Envío Estándar - Productos Especiales',
  'Estándar Rápido',
  'Agen SPX Express',
  'Anteraja Economy',
  'Anteraja Reguler',
  'Bluebird Kirim',
  'GoSend Instant',
  'GoSend Same Day',
  'GrabExpress Instant',
  'GrabExpress Sameday',
  'ID Express',
  'J&T Cargo',
  'J&T Economy',
  'J&T Express',
  'J&T Jemari',
  'JNE Reguler',
  'JNE Trucking (JTR)',
  'JNE YES',
  'Jasa Kirim Toko',
  'Ninja Xpress',
  'SPX Express Point',
  'SPX Hemat',
  'SPX Instant',
  'SPX Sameday',
  'SPX Standard',
  'Sentral Cargo',
  'SiCepat Halu',
  'SiCepat REG',
  'Sicepat Gokil',
  'Quality Post',
  '5-Day Delivery',
  'BEST Cargo',
  'Economy Delivery (Sea Shipping)',
  'Flash Express',
  'J&T Cargo (>20kg)',
  'Ninja Van',
  'POS Malaysia',
  'Poslaju',
  'SPX Express (East Malaysia)',
  'SPX Express (Sea Shipping)',
  'SPX Express (West Malaysia)',
  'Same Day Delivery',
  'Self Collection Point (SPX Express)',
  "Seller's Own Fleet (West Malaysia)",
  'Standard Delivery',
  'Standard Delivery (HK)',
  'Virtual Goods',
  'Economy International via Sea Shipping',
  'Express International (5DD)',
  'Other Logistics Provider',
  'SPX Express',
  'Shopee Self Pick-up',
  'Standard International',
  'Standard International (HK)',
  'XDE Logistics',
  'YTO Express',
  '5-Day Delivery (SPX)',
  'Doorstep Delivery (Overseas HK)',
  'Doorstep Delivery (Overseas)',
  'Doorstep Delivery (Sea Shipping)',
  'Doorstep Delivery (Warehouse)',
  'MYWH-SG',
  'Next Day Doorstep Delivery',
  'Next Day Doorstep Delivery (Warehouse)',
  'Ninja Van (Home Delivery)',
  'Pick Locker',
  'Pick Locker (Overseas)',
  "Seller's Own Fleet Delivery",
  'Shopee Collection Point',
  'Shopee Collection Point (Overseas)',
  'Virtual Goods - delivery through app/emails',
  '5-Day Delivery - ส่งไวภายใน5วัน',
  'Best Express Bulky',
  'Buyer Self-Collect ผู้ซื้อไปรับสินค้าเอง',
  'DHL Domestic Bulky',
  'EMS - Thailand Post',
  'Flash Express Bulky',
  'International Express - ส่งจากต่างประเทศ',
  'International Express - ส่งจากต่างประเทศ (HK)',
  'International Express - ส่งจากต่างประเทศ (NN)',
  'International Premium - ส่งจากต่างประเทศ (ด่วนพิเศษ)',
  'Kerry',
  'SPX Express - FS',
  'Seller Own Fleet',
  'ผู้ขายทำการจัดส่งให้',
  '4 Day Delivery',
  '4 Day Delivery (NN)',
  'GHN - Hàng Cồng Kềnh',
  'Giao Hàng Nhanh',
  'Seller Own Fleet - Vận chuyển quốc tế',
  'Standard Express',
  'Standard Express (NN WHS)',
  'VNPost Nhanh',
  'Reguler (Cashless)',
  'J\u0000026T Express',
].map((value) => ({ value, label: value }));

export default SpecifyExpressType;
